import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, Toast, ExcelIcon, exportToExcel,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import { C360Table, ColumnChooser, ViewIcon } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { getAuthHeaders, isAdmin } from '../utils/auth';
import { timedFetch } from '../utils/perfMonitor';

const perfTargets = [
  { label: 'Kurallar', url: '/api/Rules?$top=1' },
  { label: 'Workflow', url: '/api/WorkflowDefinitions?$top=1' },
  { label: 'Roller', url: '/api/Roles/Select' },
  { label: 'Kullanıcılar', url: '/api/Users/Select' },
  { label: 'Sync', url: '/api/sync/delta?since=2026-01-01T00:00:00Z&deviceId=telemetry&entityType=ProductGroup' },
];

// Bu sayfa OData liste endpoint'i ile beslenmez (kaynak: /api/dashboard/manager/telemetry — MediatR/REST,
// recentSlowHandlers dizisini gömülü döndürür). Bu nedenle useC360List yerine TanStack istemci-tarafı
// liste (filtre/sıralama/sayfalama) kullanılır; backend OData GET olmadığı için controller'a dokunulmaz.
const COLUMN_FILTER_META = {
  requestName: { type: 'text', odataField: 'RequestName' },
  level: { type: 'text', odataField: 'Level' },
  message: { type: 'text', odataField: 'Message' },
};
const SEARCH_FIELDS = ['requestName', 'level', 'message'];

export function TelemetryPage() {
  usePageMeta('Telemetry', 'API sürelerini ve backend yavaş handler kayıtlarını izleyin');

  // User grubu/rolü ADMIN olan tüm kullanıcılar backend telemetry'yi görebilir.
  const canViewBackendTelemetry = isAdmin();

  const [perfRows, setPerfRows] = useState([]);
  const [telemetryData, setTelemetryData] = useState(null);
  const [telemetryError, setTelemetryError] = useState('');
  // Yusuf Fix — LİSTE AÇILIŞINDA "Kayıt bulunamadı" YANIP SÖNMESİN: veri mount'taki effect'te
  // çekiliyor, yani ilk boyama fetch'ten ÖNCE olur. loading false başlayınca tablo o ilk karede
  // boş-durum metnini gösteriyor, veri gelince listeye dönüyordu — kullanıcı "önce kayıt yok dedi,
  // sonra liste geldi" diye okuyor. Yükleme durumu ilk kareden itibaren AÇIK başlar.
  const [loading, setLoading] = useState(true);

  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState(null);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnFilters, setColumnFilters] = useState({});

  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [sortField, setSortField] = useState('timestampUtc');
  const [sortDir, setSortDir] = useState('desc');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const runPerfCheck = useCallback(async () => {
    const headers = getAuthHeaders();
    const results = [];
    for (const target of perfTargets) {
      const startedAt = performance.now();
      try {
        const res = await timedFetch(`telemetry:${target.label}`, target.url, { headers });
        results.push({ id: target.label, label: target.label, ms: Math.round(performance.now() - startedAt), status: res.status, ok: res.ok });
      } catch {
        results.push({ id: target.label, label: target.label, ms: Math.round(performance.now() - startedAt), status: 'ERR', ok: false });
      }
    }
    setPerfRows(results);
  }, []);

  const loadBackendTelemetry = useCallback(async () => {
    if (!canViewBackendTelemetry) {
      setTelemetryData(null);
      setTelemetryError('Backend telemetry için ADMIN grubu gerekli.');
      return;
    }
    try {
      setTelemetryError('');
      const res = await fetch('/api/dashboard/manager/telemetry', {
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        const apiMessage = json?.message || json?.Message || json?.errors?.[0];
        setTelemetryError(apiMessage || `Telemetry verileri yüklenemedi. HTTP ${res.status}`);
        return;
      }
      setTelemetryData(json?.data ?? json ?? {});
    } catch {
      setTelemetryError('Telemetry verileri yüklenemedi. API bağlantısını kontrol edin.');
    }
  }, [canViewBackendTelemetry]);

  const refresh = useCallback(async () => {
    setLoading(true);
    await Promise.all([runPerfCheck(), loadBackendTelemetry()]);
    setLoading(false);
  }, [loadBackendTelemetry, runPerfCheck]);

  useEffect(() => { refresh(); }, [refresh]);

  const perfSummary = useMemo(() => {
    const total = perfRows.reduce((sum, row) => sum + row.ms, 0);
    const avgMs = perfRows.length ? Math.round(total / perfRows.length) : 0;
    const maxMs = perfRows.length ? Math.max(...perfRows.map((row) => row.ms)) : 0;
    return {
      avgMs,
      maxMs,
      slowCount: perfRows.filter((row) => row.ms > 500).length,
      errorCount: perfRows.filter((row) => !row.ok).length,
    };
  }, [perfRows]);

  // recentSlowHandlers -> normalize + istemci-tarafı filtre + sıralama
  const allRows = useMemo(() => (
    (telemetryData?.recentSlowHandlers || []).map((item, index) => ({
      ...item,
      id: `${item.timestampUtc || 'row'}-${index}`,
    }))
  ), [telemetryData]);

  const filteredRows = useMemo(() => {
    let result = allRows;
    const q = filterText.trim().toLowerCase();
    if (q) {
      result = result.filter((row) => SEARCH_FIELDS.some((f) => String(row[f] ?? '').toLowerCase().includes(q)));
    }
    for (const [field, filter] of Object.entries(columnFilters)) {
      if (!filter) continue;
      const op = filter.op || 'contains';
      const needsValue = op !== 'blank' && op !== 'notBlank';
      if (needsValue && (filter.value === '' || filter.value == null)) continue;
      const needle = String(filter.value ?? '').toLowerCase();
      result = result.filter((row) => {
        const hay = String(row[field] ?? '').toLowerCase();
        switch (op) {
          case 'blank': return hay === '';
          case 'notBlank': return hay !== '';
          case 'equals': return hay === needle;
          case 'notEquals': return hay !== needle;
          case 'startsWith': return hay.startsWith(needle);
          case 'endsWith': return hay.endsWith(needle);
          case 'notContains': return !hay.includes(needle);
          case 'contains':
          default: return hay.includes(needle);
        }
      });
    }
    return result;
  }, [allRows, filterText, columnFilters]);

  const sortedRows = useMemo(() => {
    const key = sortField;
    const list = [...filteredRows];
    list.sort((a, b) => {
      const left = a[key] ?? '';
      const right = b[key] ?? '';
      if (key.toLowerCase().includes('timestamp')) {
        const lt = left ? new Date(left).getTime() : 0;
        const rt = right ? new Date(right).getTime() : 0;
        return sortDir === 'asc' ? lt - rt : rt - lt;
      }
      if (key === 'elapsedMs') {
        const lt = Number(left) || 0;
        const rt = Number(right) || 0;
        return sortDir === 'asc' ? lt - rt : rt - lt;
      }
      return sortDir === 'asc'
        ? String(left).localeCompare(String(right), 'tr-TR')
        : String(right).localeCompare(String(left), 'tr-TR');
    });
    return list;
  }, [filteredRows, sortField, sortDir]);

  const totalCount = sortedRows.length;
  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, currentPage, pageSize]);

  const applyFilterText = (text) => { setFilterText(text); setCurrentPage(1); };
  const setColumnFilter = (columnId, next) => { setColumnFilters((prev) => ({ ...prev, [columnId]: next })); setCurrentPage(1); };
  const clearColumnFilter = (columnId) => { setColumnFilters((prev) => { const copy = { ...prev }; delete copy[columnId]; return copy; }); setCurrentPage(1); };
  const applySort = (field, dir) => { setSortField(field || 'timestampUtc'); setSortDir(dir || 'desc'); setCurrentPage(1); };

  const openView = (row = selected) => { if (!row) return; setSelected(row); setModal('view'); };
  const closeModal = () => setModal(null);

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('timestampUtc', {
      header: 'Zaman', enableColumnFilter: false,
      cell: (info) => formatDateTime(info.getValue()),
    }),
    columnHelper.accessor('requestName', {
      header: 'Request', meta: { filterType: 'text' },
      cell: (info) => { const v = info.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '-'; },
    }),
    columnHelper.accessor('elapsedMs', {
      header: 'Süre', enableColumnFilter: false,
      cell: (info) => { const v = info.getValue(); return v ? `${v} ms` : '-'; },
    }),
    columnHelper.accessor('level', {
      header: 'Level', meta: { filterType: 'text' },
      cell: (info) => {
        const v = info.getValue();
        if (!v) return '-';
        const tone = levelTone(v);
        return <StatusBadge tone={tone}>{v}</StatusBadge>;
      },
    }),
    columnHelper.accessor('message', {
      header: 'Mesaj', meta: { filterType: 'text' },
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openView(row.original); }}><ViewIcon /></button>
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('timestampUtc', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: pagedRows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { headerName: 'Zaman', valueGetter: ({ row }) => formatDateTime(row.timestampUtc) },
        { field: 'requestName', headerName: 'Request' },
        { headerName: 'Süre', valueGetter: ({ row }) => (row.elapsedMs ? `${row.elapsedMs} ms` : '-') },
        { field: 'level', headerName: 'Level' },
        { field: 'message', headerName: 'Mesaj' },
      ];
      if (sortedRows.length === 0) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
      const dateStr = new Date().toISOString().slice(0, 10);
      exportToExcel(sortedRows, cols, `telemetri_backend_${dateStr}`);
      showToast('success', `${sortedRows.length} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Request, level veya mesaj ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={refresh} disabled={loading}>{loading ? 'Yükleniyor...' : 'Yenile'}</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={pagedRows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(140px, 1fr))', gap: 12, marginBottom: 16 }}>
        <MetricCard label="Frontend Ortalama" value={`${perfSummary.avgMs} ms`} />
        <MetricCard label="Frontend En Yavaş" value={`${perfSummary.maxMs} ms`} />
        <MetricCard label="Son 24 Saat (Yavaş)" value={telemetryData?.slowHandlerCountLast24Hours ?? 0} />
        <MetricCard label="Maks Süre" value={`${telemetryData?.maxSlowHandlerDurationMs ?? 0} ms`} />
      </div>

      <ListPageShell
        title="Telemetry"
        subtitle="API sürelerini ve backend yavaş handler kayıtlarını filtreleyin, sıralayın ve inceleyin."
        toolbar={toolbarNode}
        footer={footerNode}
      >
        {telemetryError ? (
          <div style={{ background: 'var(--bg-card, #ffffff)', color: '#dc2626', padding: 24 }}>
            {telemetryError}
          </div>
        ) : (
          <C360Table
            table={table}
            columnFilterMeta={COLUMN_FILTER_META}
            columnFilters={columnFilters}
            onApplyFilter={setColumnFilter}
            onClearFilter={clearColumnFilter}
            loading={loading}
            selected={selected}
            onRowClick={setSelected}
            onRowDoubleClick={openView}
            emptyText="Yavaş handler kaydı bulunamadı"
          />
        )}
      </ListPageShell>

      {modal === 'view' && selected && (
        <div style={s.overlay} onClick={closeModal}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Yavaş Handler Detay</h3>
              <button style={s.closeBtn} onClick={closeModal}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Zaman</label>
                  <input style={s.inputView} readOnly value={formatDateTime(selected.timestampUtc)} />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Süre</label>
                  <input style={s.inputView} readOnly value={selected.elapsedMs ? `${selected.elapsedMs} ms` : '-'} />
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Request</label>
                  <input style={s.inputView} readOnly value={selected.requestName || '-'} />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Level</label>
                  <input style={s.inputView} readOnly value={selected.level || '-'} />
                </div>
              </div>
              <div style={s.formRow}>
                <div style={{ ...s.formGroup, flex: 1 }}>
                  <label style={s.label}>Mesaj</label>
                  <textarea style={{ ...s.inputView, minHeight: 100, resize: 'vertical' }} readOnly value={selected.message || '-'} />
                </div>
              </div>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnSecondary} onClick={closeModal}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

function MetricCard({ label, value }) {
  return (
    <div style={{
      background: 'var(--bg-card, #ffffff)',
      borderRadius: 8,
      padding: 16,
      border: '1px solid var(--border-color, #e5e7eb)',
    }}>
      <div style={{ color: 'var(--text-secondary, #4b5563)', fontSize: 12, fontWeight: 700, marginBottom: 6 }}>{label}</div>
      <div style={{ color: 'var(--text-primary, #111827)', fontSize: 24, fontWeight: 800 }}>{value}</div>
    </div>
  );
}

function levelTone(level) {
  const v = String(level || '').toLowerCase();
  if (v.includes('error') || v.includes('fatal') || v.includes('critical')) return 'bad';
  if (v.includes('warn')) return 'wait';
  if (v.includes('info')) return 'ok';
  return 'neutral';
}

function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString('tr-TR');
}
